import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from '@/lib/leaflet-react';
import 'leaflet/dist/leaflet.css';
import { AlertTriangle, Archive, MapPin, RefreshCw } from 'lucide-react';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import type { AssetMapItem, AssetMapResponse, AssetStatus } from '@/lib/types';

type MappedAsset = AssetMapItem & { latitude: number; longitude: number };

const statusClasses: Record<AssetStatus, string> = {
  active: 'bg-green-100 text-green-800',
  available: 'bg-green-100 text-green-800',
  deployed: 'bg-blue-100 text-blue-800',
  in_use: 'bg-blue-100 text-blue-800',
  damaged: 'bg-orange-100 text-orange-800',
  retired: 'bg-gray-100 text-gray-800',
  lost: 'bg-red-100 text-red-800',
  quarantined: 'bg-red-100 text-red-800',
};

const markerColors: Record<AssetStatus, string> = {
  active: '#16a34a',
  available: '#16a34a',
  deployed: '#2563eb',
  in_use: '#2563eb',
  damaged: '#ea580c',
  retired: '#6b7280',
  lost: '#dc2626',
  quarantined: '#dc2626',
};

interface AssetsMapViewProps {
  data: AssetMapResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  onRefetch: () => void;
}

export function AssetsMapView({ data, isLoading, isError, onRefetch }: AssetsMapViewProps) {
  const mappedAssets = useMemo(() => (data?.results ?? []).filter(hasMapCoordinates), [data?.results]);
  const missingAssets = useMemo(() => data?.missing_location ?? [], [data?.missing_location]);
  const center = useMemo(
    () =>
      mappedAssets[0]
        ? ([mappedAssets[0].latitude, mappedAssets[0].longitude] as [number, number])
        : ([39.8283, -98.5795] as [number, number]),
    [mappedAssets]
  );

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[520px] w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-8 text-center space-y-4">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto" />
        <div>
          <p className="font-medium text-red-700">Failed to load asset map.</p>
          <p className="text-sm text-gray-500">Please retry or switch back to list view.</p>
        </div>
        <Button variant="outline" onClick={onRefetch}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  if (!data || data.counts.total === 0) {
    return (
      <div className="p-12 text-center">
        <Archive className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">No assets match the current filters.</p>
        <p className="text-sm text-gray-500 mt-1">Adjust filters to see assets on the map.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard label="Mapped assets" value={data.counts.mapped} tone="text-green-700" />
        <SummaryCard label="Missing coordinates" value={data.counts.missing_location} tone="text-orange-700" />
        <SummaryCard label="Total in filter" value={data.counts.total} tone="text-gray-800" />
      </div>

      {mappedAssets.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <MapPin className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="font-medium text-gray-700">No mapped assets have coordinates.</p>
          <p className="text-sm text-gray-500">Assets without coordinates are listed below.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-4">
          <div className="h-[560px] overflow-hidden rounded-lg border">
            <MapContainer center={center} zoom={mappedAssets.length === 1 ? 13 : 4} className="h-full w-full">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds assets={mappedAssets} />
              {mappedAssets.map((asset) => (
                <CircleMarker
                  key={asset.id}
                  center={[asset.latitude, asset.longitude]}
                  radius={10}
                  pathOptions={{
                    color: markerColors[asset.status],
                    fillColor: markerColors[asset.status],
                    fillOpacity: 0.85,
                    weight: 2,
                  }}
                >
                  <Popup>
                    <div className="space-y-2 min-w-48">
                      <div>
                        <Link to={`/assets/${asset.id}`} className="font-semibold text-blue-700 hover:underline">
                          {asset.label}
                        </Link>
                        <p className="font-mono text-xs text-gray-500">{asset.serial_number}</p>
                      </div>
                      <Badge className={statusClasses[asset.status]}>{asset.status_label}</Badge>
                      <p className="text-sm text-gray-700">{asset.current_location_summary?.name}</p>
                      {asset.latest_deployment && (
                        <p className="text-xs text-gray-500">
                          Last action: {asset.latest_deployment.action_label}
                        </p>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Mapped assets</CardTitle>
            </CardHeader>
            <CardContent className="max-h-[488px] overflow-y-auto space-y-3">
              {mappedAssets.map((asset) => (
                <AssetMapListItem key={asset.id} asset={asset} />
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {missingAssets.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-orange-900">
              <AlertTriangle className="h-4 w-4" />
              Assets missing map coordinates ({missingAssets.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              {missingAssets.slice(0, 12).map((asset) => (
                <div key={asset.id} className="rounded border bg-white p-3">
                  <Link to={`/assets/${asset.id}`} className="font-medium text-sm text-blue-700 hover:underline">
                    {asset.label}
                  </Link>
                  <p className="font-mono text-xs text-gray-500">{asset.serial_number}</p>
                  <Badge className={`${statusClasses[asset.status]} mt-2`}>{asset.status_label}</Badge>
                </div>
              ))}
            </div>
            {missingAssets.length > 12 && (
              <p className="text-xs text-orange-800 mt-3">
                Showing 12 of {missingAssets.length} assets missing coordinates.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function hasMapCoordinates(asset: AssetMapItem): asset is MappedAsset {
  return asset.latitude !== null && asset.longitude !== null;
}

function FitBounds({ assets }: { assets: MappedAsset[] }) {
  const map = useMap();
  const lastFitKey = useRef<string | null>(null);
  const coordinates = useMemo(
    () => assets.map((asset) => [asset.latitude, asset.longitude] as [number, number]),
    [assets]
  );
  const coordinatesKey = useMemo(
    () => coordinates.map(([latitude, longitude]) => `${latitude}:${longitude}`).join('|'),
    [coordinates]
  );

  useEffect(() => {
    if (coordinates.length === 0 || coordinatesKey === lastFitKey.current) {
      return;
    }
    lastFitKey.current = coordinatesKey;
    if (coordinates.length === 1) {
      map.setView(coordinates[0], 13);
    } else {
      map.fitBounds(coordinates, { padding: [36, 36] });
    }
  }, [coordinates, coordinatesKey, map]);

  return null;
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="rounded-lg border bg-white p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-2xl font-semibold ${tone}`}>{value}</p>
    </div>
  );
}

function AssetMapListItem({ asset }: { asset: MappedAsset }) {
  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Link to={`/assets/${asset.id}`} className="font-medium text-blue-700 hover:underline">
            {asset.label}
          </Link>
          <p className="font-mono text-xs text-gray-500">{asset.serial_number}</p>
        </div>
        <span
          className="mt-1 h-3 w-3 rounded-full border border-white shadow"
          style={{ backgroundColor: markerColors[asset.status] }}
        />
      </div>
      <Badge className={statusClasses[asset.status]}>{asset.status_label}</Badge>
      <div className="text-sm text-gray-600">
        <p>{asset.current_location_summary?.name}</p>
        <p className="font-mono text-xs">
          {asset.latitude.toFixed(6)}, {asset.longitude.toFixed(6)}
        </p>
      </div>
    </div>
  );
}
